import {
  CellDoubleClickedEvent,
  ColDef,
  GridOptions,
} from '@ag-grid-community/core'
import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core'
import {
  heliosDefaultColDef,
  heliosSizingAdjustments,
} from '@helios/ag-grid-config'
import { AgGridApi, NfColumnTypes } from '@northfork/ag-grid'
import { InlineEditDataType } from '@northfork/ag-grid-angular'
import { composeFieldPath } from '@northfork/basics'
import { Currency } from '../../../../model/currency.model'
import { CashEntryMatchRow } from '../../../../model/single-transaction/cash-entry/cash-entry-match.model'
import { CellMatched } from '../../../../model/single-transaction/cell-matched.model'
import { TransactionMatch } from '../../../../model/single-transaction/transaction-match'
import { createCashEntryMatchFromTransactionMatch } from '../../../../util/cash-data-mapper-util'

@Component({
  selector: 'hs-cash-entry-matches',
  templateUrl: './cash-entry-matches.component.html',
  styleUrls: ['./cash-entry-matches.component.scss'],
})
export class CashEntryMatchesComponent implements OnInit {
  @Input() matches?: TransactionMatch[]
  @Input() currencyList?: Currency[]
  @Input() accountDisplayName?: string
  loading = true

  @Output() matchCellDoubleClick = new EventEmitter<CellMatched>()

  rowData: CashEntryMatchRow[] = []
  cashEntryField = composeFieldPath(this.rowData)

  gridOptions: GridOptions = this.createGridOptions()

  gridApi?: AgGridApi

  ngOnInit() {
    this.rowData = this.createFreshRowData()
    this.loading = false
  }

  private createGridOptions(): GridOptions {
    return {
      ...heliosSizingAdjustments,
      onGridReady: event => {
        this.gridApi = event
        event.api.setDomLayout('autoHeight')
      },
      defaultColDef: {
        ...heliosDefaultColDef,
        menuTabs: [],
        sortable: false,
      },
      sideBar: false,
      onCellDoubleClicked: (event: CellDoubleClickedEvent) => {
        this.matchCellDoubleClick.emit({
          field: event.column.getColId(),
          value: event.data[event.column.getColId()],
        })
      },
      getContextMenuItems: () => this.getContextMenuItems(),
      columnDefs: this.createColumnDefs(),
      onFirstDataRendered: params => {
        params.api.sizeColumnsToFit()
      },
    }
  }

  /**
   * The right click magic
   *
   * @internal
   */
  getContextMenuItems() {
    return ['copy']
  }

  private createColumnDefs(): ColDef[] {
    const colDef = [
      {
        field: this.cashEntryField('account'),
        type: NfColumnTypes.Identifier,
        width: 220,
      },
      {
        field: this.cashEntryField('category'),
        type: NfColumnTypes.Identifier,
        width: 230,
      },
      {
        field: this.cashEntryField('entryDate'),
        type: NfColumnTypes.Identifier,
        width: 90,
      },

      {
        field: this.cashEntryField('settleDate'),
        type: NfColumnTypes.Identifier,
        width: 90,
      },
      {
        field: this.cashEntryField('postDate'),
        type: NfColumnTypes.Identifier,
        width: 90,
      },
      {
        field: this.cashEntryField('cusip'),
        type: NfColumnTypes.Identifier,
        width: 100,
      },
      {
        field: this.cashEntryField('currency'),
        type: NfColumnTypes.Identifier,
        width: 70,
      },
      {
        field: this.cashEntryField('amount'),
        type: NfColumnTypes.Number,
        width: 110,
      },
      {
        field: this.cashEntryField('inventoryType'),
        type: NfColumnTypes.Identifier,
        width: 110,
      },
      {
        field: this.cashEntryField('isCapitalize'),
        type: InlineEditDataType.Checkbox,
        width: 70,
      },
    ]
    return colDef
  }

  createFreshRowData(): CashEntryMatchRow[] {
    const matchRowData: CashEntryMatchRow[] = []
    this.matches?.forEach(match => {
      matchRowData.push(
        createCashEntryMatchFromTransactionMatch(
          match,
          this.accountDisplayName ?? '',
          this.currencyList ?? [],
        ),
      )
    })
    return matchRowData
  }
}
